<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import type { BookingOption } from '@/types/products';

import { ICON_SIZE_MD } from '@/composables/booking/booking-widget.constants';
import { useDropdown } from '@/composables/useDropdown.ts';
import { useBookingWidgetContext } from '@/types/booking-widget-context';
import { formatPrice, getLocalizedLabel } from '@/utils/utils';

import MobileModal from '../MobileModal.vue';

const context = useBookingWidgetContext();

const isMobile = context.bookingWidget.isMobile;
const isOpen = ref(false);
const dropdown = ref<HTMLElement>();

const defaultOption = computed(() => {
  return context.activityDetails!.options.find(opt => opt.isDefault) ?? context.activityDetails!.options[0];
});

const selectedOption = ref<BookingOption>(defaultOption.value);
const tempSelectedOption = ref<BookingOption>(defaultOption.value);

function getOptionLabel(option: BookingOption): string {
  return getLocalizedLabel(option.labels, option.label, context.bookingWidget.currentLocale.value);
}

function getMinPrice(option: BookingOption) {
  if (option.units.length === 0)
    return 0;
  const prices = option.units.map(unit => unit.price);
  return Math.min(...prices);
}

const { openDropdown, closeDropdown } = useDropdown({
  isOpen,
  dropdownRef: dropdown,
  isMobile,
  onOpen: () => {
    tempSelectedOption.value = selectedOption.value;
  },
});

function selectOption(option: BookingOption) {
  if (isMobile.value) {
    tempSelectedOption.value = option;
    return;
  }
  selectedOption.value = option;
  tempSelectedOption.value = option;
  context.bookingWidget.handleOptionSelected(option);
  closeDropdown();
}

function applyMobileSelection() {
  selectedOption.value = tempSelectedOption.value;
  context.bookingWidget.handleOptionSelected(tempSelectedOption.value);
  closeDropdown();
}

watch(
  () => context.activityDetails,
  (activityDetails) => {
    if (activityDetails && activityDetails.options.length > 0) {
      const newDefaultOption = activityDetails.options.find(opt => opt.isDefault) ?? activityDetails.options[0];
      selectedOption.value = newDefaultOption;
      tempSelectedOption.value = newDefaultOption;
      context.bookingWidget.handleOptionSelected(newDefaultOption);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div ref="dropdown" class="booking-options-dropdown">
    <button
      class="dropdown-button"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="openDropdown"
    >
      <span class="button-content">
        <span class="option-label">{{ getOptionLabel(selectedOption) }}</span>
        <span class="option-price">{{ $t('booking.from') }} <strong>{{ formatPrice(getMinPrice(selectedOption), context.activityDetails!.currency) }}</strong></span>
      </span>
      <ChevronDown :size="ICON_SIZE_MD" :class="{ rotate: isOpen && !isMobile }" aria-hidden="true" />
    </button>

    <!-- Desktop Dropdown -->
    <div v-if="!isMobile && isOpen" class="dropdown-menu" role="radiogroup" :aria-label="$t('booking.chooseYourActivity')">
      <div
        v-for="option in context.activityDetails!.options"
        :key="option.id"
        class="option-item"
        :class="{ selected: option.id === tempSelectedOption.id }"
        role="radio"
        :aria-checked="option.id === tempSelectedOption.id"
        tabindex="0"
        @click="selectOption(option)"
        @keydown.enter="selectOption(option)"
        @keydown.space.prevent="selectOption(option)"
      >
        <div class="radio-button" :class="{ checked: option.id === tempSelectedOption.id }" aria-hidden="true" />
        <div class="option-content">
          <div class="option-name">
            {{ getOptionLabel(option) }}
          </div>
          <div class="option-price-text">
            {{ $t('booking.from') }} <strong>{{ formatPrice(getMinPrice(option), context.activityDetails!.currency) }}</strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Modal -->
    <MobileModal
      v-if="isMobile"
      :is-open="isOpen"
      :title="$t('booking.chooseYourActivity')"
      @close="closeDropdown"
    >
      <template #body>
        <div class="options-group" role="radiogroup" :aria-label="$t('booking.chooseYourActivity')">
          <div
            v-for="option in context.activityDetails!.options"
            :key="option.id"
            class="option-card"
            :class="{ selected: option.id === tempSelectedOption.id }"
            role="radio"
            :aria-checked="option.id === tempSelectedOption.id"
            tabindex="0"
            @click="selectOption(option)"
            @keydown.enter="selectOption(option)"
            @keydown.space.prevent="selectOption(option)"
          >
            <div class="radio-button" :class="{ checked: option.id === tempSelectedOption.id }" aria-hidden="true" />
            <div class="option-content">
              <div class="option-name">
                {{ getOptionLabel(option) }}
              </div>
              <div class="option-price-text">
                {{ $t('booking.from') }} <strong>{{ formatPrice(getMinPrice(option), context.activityDetails!.currency) }}</strong>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="yb-apply-button apply-button-options" @click="applyMobileSelection">
          {{ $t('booking.apply') }}
        </button>
      </template>
    </MobileModal>
  </div>
</template>

<style scoped lang="css">
.booking-options-dropdown {
  position: relative;
  width: 100%;
}

.dropdown-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 74px;
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  font-size: var(--yb-text-sm);
  outline-color: var(--yb-outline);

  .button-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .option-label {
    font-weight: var(--yb-text-semi-bold);
    color: var(--yb-color-black);
  }

  .option-price {
    font-size: var(--yb-text-xs);
    color: var(--yb-color-black);
  }

  svg {
    flex-shrink: 0;
    transition: transform 0.2s ease-out;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0px 4px 8px 0px;
  z-index: 10;
  max-height: 400px;
  overflow-y: auto;
  padding: var(--yb-spacing-xs);
  animation: fadeIn 0.2s ease-out;
}

.options-group {
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-sm);
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: var(--yb-spacing-sm);
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--yb-color-lightgray);
  }

  &.selected {
    background-color: var(--yb-color-lightgray);
  }
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid var(--yb-color-mediumgray);
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 2px;
  position: relative;
  transition: border-color 0.15s ease;

  &.checked {
    border-color: var(--yb-color-primary);

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--yb-color-primary);
    }
  }
}

.option-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.option-name {
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
  font-size: var(--yb-text-sm);
}

.option-price-text {
  font-size: var(--yb-text-xs);
  color: var(--yb-color-black);
}

.option-card {
  display: flex;
  align-items: flex-start;
  gap: var(--yb-spacing-sm);
  padding: var(--yb-spacing-base);
  border: 2px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  transition: border-color 0.15s ease;

  &.selected {
    border-color: var(--yb-color-primary);
  }

  &:active {
    background-color: var(--yb-color-lightgray);
  }
}

.apply-button-options {
  padding: var(--yb-spacing-base);
  height: auto;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-menu {
    animation: none;
  }

  .dropdown-button svg,
  .option-item,
  .radio-button,
  .option-card,
  .apply-button-options {
    transition: none;
  }
}
</style>
